import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { NotificationBell } from '../components/NotificationBell';
import { WeeklyBarChart } from '../components/WeeklyBarChart';
import { MedicalFilesPanel } from '../components/MedicalFilesPanel';
import {
  HandHeart, MapPin, Phone, User, Calendar, CheckCircle2,
  MessageSquare, RefreshCw, LogOut, TrendingUp, FileText,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  pending:   'bg-amber-100 text-amber-800',
  contacted: 'bg-emerald-100 text-emerald-800',
  approved:  'bg-blue-100 text-blue-800',
  declined:  'bg-red-100 text-red-800',
};

const STATUS_LABELS = {
  pending:   { ar: 'قيد التواصل',    en: 'Pending contact' },
  contacted: { ar: 'تم التواصل',      en: 'Contacted'        },
  approved:  { ar: 'مقبول',           en: 'Approved'         },
  declined:  { ar: 'مرفوض',           en: 'Declined'         },
};

/**
 * Physio Manager dashboard — shows every paid physio booking so the ops team
 * can reach out externally (phone/WhatsApp) to arrange the actual session.
 * Only accessible to the `physio_manager` role (seeded on backend startup).
 */
export const PhysioManagerDashboard = () => {
  const { t, language } = useLanguage();
  const { user, logout } = useAuth();
  const isRTL = language === 'ar';

  const [bookings, setBookings] = useState([]);
  const [filesFor, setFilesFor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [b, a] = await Promise.all([
        axios.get(`${API}/physio/bookings`, { withCredentials: true }),
        axios.get(`${API}/physio-manager/analytics`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setBookings(b.data || []);
      setAnalytics(a.data);
    } catch (e) {
      toast.error(t('تعذّر تحميل الحجوزات', 'Failed to load bookings'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [b, a] = await Promise.all([
          axios.get(`${API}/physio/bookings`, { withCredentials: true }),
          axios.get(`${API}/physio-manager/analytics`, { withCredentials: true }).catch(() => ({ data: null })),
        ]);
        if (!cancelled) {
          setBookings(b.data || []);
          setAnalytics(a.data);
        }
      } catch {
        if (!cancelled) toast.error(t('تعذّر تحميل الحجوزات', 'Failed to load bookings'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [t]);

  const markContacted = async (b) => {
    setBusyId(b._id);
    try {
      await axios.post(`${API}/physio/bookings/${b._id}/mark-contacted`, {}, { withCredentials: true });
      setBookings((prev) => prev.map((x) => (x._id === b._id ? { ...x, status: 'contacted' } : x)));
      toast.success(t('تم وسم الحجز كمتواصل معه', 'Marked as contacted'));
    } catch {
      toast.error(t('فشل التحديث', 'Update failed'));
    } finally {
      setBusyId(null);
    }
  };

  const regionLabel = (region) => {
    if (region === 'central_bekaa') return t('بقاع أوسط', 'Central Bekaa');
    if (region === 'western_bekaa') return t('بقاع غربي', 'Western Bekaa');
    return region || '—';
  };

  return (
    <div className={`min-h-screen bg-gradient-to-b from-blue-50 to-white ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'} data-testid="physio-manager-dashboard">
      <header className="bg-white shadow-sm sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#22c8b8] to-[#1565c0] text-white flex items-center justify-center">
              <HandHeart size={20} />
            </div>
            <div>
              <h1 className="text-lg font-extrabold text-[#0d2c54] leading-tight">
                {t('لوحة مدير العلاج الفيزيائي', 'Physio Manager Dashboard')}
              </h1>
              <p className="text-[11px] text-slate-500">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <Button
              variant="outline"
              size="sm"
              onClick={refresh}
              className="rounded-full"
              data-testid="pm-refresh"
            >
              <RefreshCw size={14} className="mr-1" />
              {t('تحديث', 'Refresh')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={logout}
              className="rounded-full text-red-600 hover:text-red-700"
              data-testid="pm-logout"
            >
              <LogOut size={14} />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          {[
            { key: 'total',     ar: 'إجمالي', en: 'Total',     val: bookings.length,                                   color: 'bg-slate-100 text-slate-700' },
            { key: 'pending',   ar: 'قيد التواصل', en: 'Pending', val: bookings.filter((b) => b.status === 'pending').length, color: 'bg-amber-100 text-amber-800' },
            { key: 'contacted', ar: 'تم التواصل', en: 'Contacted', val: bookings.filter((b) => b.status === 'contacted').length, color: 'bg-emerald-100 text-emerald-800' },
          ].map((s) => (
            <Card key={s.key} className={`p-4 border-0 ${s.color}`} data-testid={`pm-stat-${s.key}`}>
              <p className="text-xs font-bold uppercase tracking-wider">{t(s.ar, s.en)}</p>
              <p className="text-3xl font-extrabold mt-1">{s.val}</p>
            </Card>
          ))}
        </div>

        {/* Weekly chart */}
        {analytics && analytics.weeks && analytics.weeks.length > 0 && (
          <Card className="p-5 mb-6" data-testid="pm-analytics-card">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <TrendingUp size={16} className="text-[#1565c0]" />
                <h3 className="font-bold text-slate-800">
                  {t('الحجوزات المدفوعة أسبوعياً', 'Paid bookings per week')}
                </h3>
              </div>
              <span className="text-xs text-slate-500">
                {t('آخر 8 أسابيع', 'Last 8 weeks')} — {t('الإجمالي', 'Total')}:{' '}
                <span className="font-bold text-slate-800">{analytics.total}</span>
              </span>
            </div>
            <WeeklyBarChart
              data={analytics.weeks.map((w) => ({
                // Show M-DD only (chart is narrow), full range in the tooltip.
                label: w.week_start.slice(5),
                value: w.count,
              }))}
              color="#1565c0"
              height={150}
              testId="pm-weekly-chart"
            />
          </Card>
        )}

        {loading ? (
          <div className="text-center text-slate-500 py-16 text-sm">
            {t('جاري التحميل...', 'Loading...')}
          </div>
        ) : bookings.length === 0 ? (
          <Card className="p-10 text-center" data-testid="pm-empty">
            <HandHeart className="text-slate-300 mx-auto mb-3" size={40} />
            <p className="text-slate-700 font-bold">
              {t('لا توجد حجوزات مدفوعة حتى الآن', 'No paid bookings yet')}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {t('ستظهر هنا فور دفع المريض للباقة وإرسال معلوماته.', 'They appear here as soon as a patient pays and submits their info.')}
            </p>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="pm-bookings-list">
            {bookings.map((b) => {
              const label = STATUS_LABELS[b.status] || { ar: b.status, en: b.status };
              return (
                <Card key={b._id} className="p-4" data-testid={`pm-booking-${b._id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className="w-10 h-10 rounded-full bg-blue-100 text-[#1565c0] flex items-center justify-center flex-shrink-0">
                        <User size={18} />
                      </div>
                      <div className="min-w-0">
                        <p className="font-bold text-[#0d2c54] truncate">{b.patient_name || '—'}</p>
                        <p className="text-xs text-slate-500 flex items-center gap-1">
                          <Phone size={11} /> {b.patient_phone || '—'}
                        </p>
                        <p className="text-xs text-slate-600 mt-1">
                          {language === 'ar' ? b.package_title_ar : (b.package_title_en || b.package_title_ar)}
                          {b.session_number && (
                            <span className="ml-1 text-[10px] text-slate-500">
                              ({t('جلسة', 'session')} {b.session_number})
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${STATUS_STYLES[b.status] || 'bg-slate-100 text-slate-700'}`}>
                      {t(label.ar, label.en)}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-4 text-sm">
                    <div className="flex items-center gap-2 text-slate-700">
                      <Calendar size={14} className="text-slate-400" />
                      <span>{b.preferred_date} {b.preferred_time}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-700">
                      <MapPin size={14} className="text-slate-400" />
                      <span>{regionLabel(b.region)} — {b.city} / {b.street}</span>
                    </div>
                    {b.address_details && (
                      <div className="md:col-span-2 flex items-start gap-2 text-slate-600 text-xs">
                        <MapPin size={13} className="text-slate-300 mt-0.5" />
                        <span>{b.address_details}</span>
                      </div>
                    )}
                    {b.condition_description && (
                      <div className="md:col-span-2 flex items-start gap-2 text-slate-600 text-xs">
                        <MessageSquare size={13} className="text-slate-300 mt-0.5" />
                        <span><b>{t('الحالة:', 'Condition:')}</b> {b.condition_description}</span>
                      </div>
                    )}
                    {b.notes && (
                      <div className="md:col-span-2 flex items-start gap-2 text-slate-600 text-xs">
                        <MessageSquare size={13} className="text-slate-300 mt-0.5" />
                        <span><b>{t('ملاحظات:', 'Notes:')}</b> {b.notes}</span>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setFilesFor(filesFor === b.patient_id ? null : b.patient_id)}
                      className="h-9 px-3 rounded-full text-sm font-bold"
                      data-testid={`pm-files-toggle-${b._id}`}
                    >
                      <FileText size={14} className="mr-1" />
                      {filesFor === b.patient_id
                        ? t('إخفاء الملفات', 'Hide files')
                        : t('ملفات المريض', 'Patient files')}
                    </Button>
                  </div>

                  {filesFor === b.patient_id && (
                    <div className="mt-3 border-t pt-3" data-testid={`pm-files-panel-${b._id}`}>
                      <MedicalFilesPanel patientId={b.patient_id} readOnly />
                    </div>
                  )}

                  {b.status === 'pending' && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {b.patient_phone && (
                        <a
                          href={`tel:${b.patient_phone}`}
                          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold"
                          data-testid={`pm-call-${b._id}`}
                        >
                          <Phone size={14} /> {t('اتصل بالمريض', 'Call patient')}
                        </a>
                      )}
                      {b.patient_phone && (
                        <a
                          href={`https://wa.me/${b.patient_phone.replace(/\D/g, '')}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-[#25D366] hover:bg-[#1ebe57] text-white text-sm font-bold"
                          data-testid={`pm-whatsapp-${b._id}`}
                        >
                          <MessageSquare size={14} /> {t('واتساب', 'WhatsApp')}
                        </a>
                      )}
                      <Button
                        onClick={() => markContacted(b)}
                        disabled={busyId === b._id}
                        className="h-9 px-3 rounded-full bg-[#1565c0] hover:bg-[#0d47a1] text-white text-sm font-bold"
                        data-testid={`pm-mark-contacted-${b._id}`}
                      >
                        <CheckCircle2 size={14} className="mr-1" />
                        {busyId === b._id
                          ? t('جاري...', 'Saving...')
                          : t('وسم كمتواصل', 'Mark contacted')}
                      </Button>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default PhysioManagerDashboard;
